/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      // Only submit the filter when a type is actually selected. An empty
      // filter.p.product_type= is not a no-op to Shopify.
      this.productTypeInput.disabled = this.productTypeInput.value === '';

      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Builds the query string for the predictive (suggest) endpoint.
     * @param {string} searchTerm - Search query.
     * @returns {string}
     */
    getPredictiveParams(searchTerm) {
      let searchParams = `q=${encodeURIComponent(this.getScopedTerm(searchTerm))}`;

      // Without this, only products come back and the query suggestions, collection,
      // page and article tabs the results section renders stay permanently empty.
      searchParams += `&${encodeURIComponent('resources[type]')}=query,product,collection,page,article`;
      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${theme.settings.pSearchFields}`;
      searchParams += '&section_id=predictive-search';

      return searchParams;
    }

    /**
     * Builds the query string for the full search endpoint, used as a fallback.
     * @param {string} searchTerm - Search query.
     * @returns {string}
     */
    getFallbackParams(searchTerm) {
      let searchParams = `q=${encodeURIComponent(this.getScopedTerm(searchTerm))}`;
      searchParams += '&type=product';
      // Match the last word as a prefix, so results appear while the term is still being typed.
      searchParams += `&${encodeURIComponent('options[prefix]')}=last`;
      // No options[fields]: /search returns nothing at all when it is set. Keep this
      // request identical to what the search form posts.
      searchParams += '&section_id=predictive-search';

      return searchParams;
    }

    /**
     * Builds the query string that renders cards for a known set of products.
     * @param {number[]} ids - Product ids, in the order they should appear.
     * @returns {string}
     */
    // eslint-disable-next-line class-methods-use-this
    getByIdParams(ids) {
      const query = ids.map((id) => `id:${id}`).join(' OR ');
      return `q=${encodeURIComponent(query)}&type=product&section_id=predictive-search`;
    }

    /**
     * Scopes a search term to the selected product type, if there is one.
     * @param {string} searchTerm - Search query.
     * @returns {string}
     */
    getScopedTerm(searchTerm) {
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        return `product_type:${this.productTypeInput.value} AND ${searchTerm}`;
      }
      return searchTerm;
    }

    /**
     * Fetches a results section and returns its markup.
     * @param {string} url - URL to fetch.
     * @returns {Promise<?string>} the section markup, or null if it rendered nothing.
     */
    async fetchResults(url) {
      const response = await fetch(url);
      if (!response.ok) throw new Error(response.status);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();

      const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
      if (!resultsEl || !resultsEl.querySelector('[data-has-products]')) return null;

      return resultsEl.innerHTML;
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      const predictiveParams = this.getPredictiveParams(searchTerm);
      const queryKey = predictiveParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        let markup = await this.fetchResults(`${theme.routes.predictiveSearch}?${predictiveParams}`);

        // Three stages, cheapest first, each only reached when the one before found no
        // products. Whatever finds products wins, so the dropdown can never show less than
        // the stage below it would have.
        if (markup === null || markup.includes('data-has-products="false"')) {
          // 2. The full search engine - the one the search page uses, so this also picks up
          //    Search & Discovery synonyms and boosts that the suggest endpoint does not.
          const fallback = await this.fetchResults(
            `${theme.routes.searchUrl}?${this.getFallbackParams(searchTerm)}`
          );

          if (fallback && fallback.includes('data-has-products="true"')) {
            markup = fallback;
          } else {
            // 3. Fragments. Neither engine matches part of a word or part of an item number,
            //    so the catalogue index in the browser is asked and the products it names are
            //    then rendered by id.
            const local = await this.getLocalResults(searchTerm);
            if (local) markup = local;
          }
        }

        if (markup === null) {
          this.close();
          return;
        }

        const resultsMarkup = markup.replace(/psearch/g, this.input.id);
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Asks the browser-side index for products, then has Shopify render their cards.
     * @param {string} searchTerm - Search query.
     * @returns {Promise<?string>} Section markup, or null if nothing matched.
     */
    async getLocalResults(searchTerm) {
      if (!window.ZimpleSearchIndex) return null;

      // A product type filter is a real restriction on the results, and the index has no way
      // to honour it. Better to show the engine's answer than a wider one that ignores it.
      if (this.productTypeInput && this.productTypeInput.value !== '') return null;

      const hits = await window.ZimpleSearchIndex.match(searchTerm, theme.settings.pSearchLimit);
      if (!hits.length) return null;

      const markup = await this.fetchResults(
        `${theme.routes.searchUrl}?${this.getByIdParams(hits.map((hit) => hit.i))}`
      );
      if (!markup || !markup.includes('data-has-products="true"')) return null;

      return this.constructor.reorder(markup, hits.map((hit) => hit.i));
    }

    /**
     * Puts the rendered cards back into the index's order. An "id:" query carries no relevance
     * of its own, so Shopify returns them in an order that means nothing here.
     * @param {string} markup - Section markup.
     * @param {number[]} ids - Product ids, best first.
     * @returns {string}
     */
    static reorder(markup, ids) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = markup;

      const list = tmpl.content.querySelector('#psearch-products .predictive-search__list');
      if (!list) return markup;

      const items = Array.from(list.querySelectorAll('[data-product-id]'));
      if (items.length < 2) return markup;

      // The "view all" row is the last child and has no product id, so appending the products
      // in order in front of it leaves it where it belongs.
      const viewAll = list.querySelector('.js-submit');
      ids.forEach((id) => {
        const item = items.find((el) => el.dataset.productId === String(id));
        if (item) list.insertBefore(item, viewAll);
      });

      return tmpl.innerHTML;
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
